import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import MenuItemCard from './MenuItemCard';

/**
 * MenuPage — shows category tabs at the top,
 * and item cards for the selected category.
 * Includes a search bar for filtering.
 */
export default function MenuPage({ categories, restaurant, onSelectItem }) {
  const { t, loc } = useApp();
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const catBarRef = useRef(null);

  // Scroll active category pill into view
  useEffect(() => {
    if (catBarRef.current) {
      const active = catBarRef.current.querySelector('[data-active="true"]');
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeCategory]);

  // Build filtered items
  const allItems = categories.flatMap((cat) =>
    cat.items.map((item) => ({ ...item, categoryId: cat.id, categoryName: cat.name }))
  );

  const filteredItems =
    activeCategory === 'all'
      ? allItems
      : allItems.filter((item) => item.categoryId === activeCategory);

  const searchedItems = search.trim()
    ? filteredItems.filter((item) => {
        const term = search.toLowerCase();
        const name = loc(item.name).toLowerCase();
        const desc = loc(item.description).toLowerCase();
        return name.includes(term) || desc.includes(term);
      })
    : filteredItems;

  return (
    <div style={styles.container}>
      {/* Search bar */}
      <div style={styles.searchWrap}>
        <span style={styles.searchIcon}>🔍</span>
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        {search && (
          <button onClick={() => setSearch('')} style={styles.clearSearch}>
            ✕
          </button>
        )}
      </div>

      {/* Category tabs — horizontal scroll */}
      <div ref={catBarRef} className="hide-scrollbar" style={styles.catBar}>
        <button
          data-active={activeCategory === 'all' ? 'true' : 'false'}
          onClick={() => setActiveCategory('all')}
          style={{
            ...styles.catPill,
            ...(activeCategory === 'all' ? styles.catPillActive : {}),
          }}
        >
          ✦ {t('allCategories')}
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            data-active={activeCategory === cat.id ? 'true' : 'false'}
            onClick={() => setActiveCategory(cat.id)}
            style={{
              ...styles.catPill,
              ...(activeCategory === cat.id ? styles.catPillActive : {}),
            }}
          >
            {cat.icon} {loc(cat.name)}
          </button>
        ))}
      </div>

      {/* Items grid */}
      <div style={styles.itemsGrid}>
        {searchedItems.length === 0 ? (
          <div style={styles.noResults}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔍</div>
            <p>{t('noResults')}</p>
          </div>
        ) : (
          searchedItems.map((item, i) => (
            <MenuItemCard
              key={item.id}
              item={item}
              restaurant={restaurant}
              onClick={() => onSelectItem(item)}
              index={i}
            />
          ))
        )}
      </div>

      {/* Bottom spacer for cart button */}
      <div style={{ height: '100px' }} />
    </div>
  );
}

const styles = {
  container: {
    paddingBottom: '1rem',
    animation: 'fadeIn 0.3s ease',
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    margin: '0.75rem 1rem',
    padding: '0.6rem 1rem',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: 'var(--radius-full)',
    gap: '0.5rem',
    border: '1.5px solid var(--border-light)',
    transition: 'all var(--transition)',
  },
  searchIcon: {
    fontSize: '0.85rem',
    opacity: 0.5,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '0.9rem',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
  },
  clearSearch: {
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--border)',
  },
  catBar: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.5rem 1rem 0.75rem',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  catPill: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.5rem 1rem',
    borderRadius: 'var(--radius-full)',
    fontSize: '0.82rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    border: '1.5px solid var(--border)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-secondary)',
    transition: 'all var(--transition-fast)',
  },
  catPillActive: {
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    borderColor: 'var(--accent)',
    fontWeight: 600,
    boxShadow: '0 2px 12px var(--accent-glow)',
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0.75rem',
    padding: '0 1rem',
  },
  noResults: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: 'var(--text-muted)',
    fontSize: '0.9rem',
  },
};
